import classnames from 'classnames';
import moment from 'moment';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import AssignmentListCard from '~/containers/ApplicationShiftDetails/AssignmentListCard';
import CancellationCard from '~/containers/ApplicationShiftDetails/CancellationCard';
import LocationCard from '~/containers/ApplicationShiftDetails/LocationCard';
import ShiftLeaderCard from '~/containers/ApplicationShiftDetails/ShiftLeaderCard';
import ShiftsTurnLeadersCard from '~/containers/ApplicationShiftDetails/ShiftsTurnLeadersCard';
import { usePublisherMe } from '~/hooks/api/usePublisherMe';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { Turn, useShift } from '~/hooks/api/useShift';
import Spacings from '~/modules/ui-kit/spacings';
import PrecedingAndFollowingTeams from '../../components/PreceedingAndFollowingTeams';
import { getPhoneNumberUri } from '../../utils/phone-number-helpers';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, Link } from '@tanstack/react-router';

const route = getRouteApi('/application/$year/$week/$day/{-$shiftId}');

export const ApplicationShiftDetails = () => {
  const params = route.useParams();
  const theme = useTheme();
  const { data: shift, isFetching: isFetchingShift } = useShift({
    id: parseInt(params.shiftId ?? '', 10),
    expand: [
      'applications.turn.turnReport',
      'applications.turn.route.depot',
      'applications.turn.turnAssignments.publisher',
      'applications.publisher',
    ],
  });
  const { data: reportingSettings, isFetching: isFetchingReportingSettings } =
    useSettingsByCategory('reporting');
  const { data: planningSettings, isFetching: isFetchingPlanningSettings } =
    useSettingsByCategory('planning');
  const { data: masterDataSettings, isFetching: isFetchingMasterDataSettings } =
    useSettingsByCategory('masterData');

  const { data: publisher, isFetching: isFetchingPublisher } = usePublisherMe();

  const isFetching =
    isFetchingShift ||
    isFetchingPublisher ||
    isFetchingReportingSettings ||
    isFetchingPlanningSettings ||
    isFetchingMasterDataSettings;

  const application = shift
    ? shift.applications?.find((_) => _.publisher.id === publisher?.id)
    : undefined;

  const shiftTookPlace = moment().isAfter(shift && shift.startDate);
  const turn = application && application.turn;
  const publisherIsAssignedForThisShift = Boolean(application);

  const getShiftLeader = useCallback((turn?: Turn) => {
    const leaderAssignment =
      turn && turn.turnAssignments?.find((assignment) => assignment.isLead);
    return (
      leaderAssignment && {
        ...leaderAssignment.publisherMiniRecord,
        experienceLevel: leaderAssignment.publisher?.experienceLevel,
        birthDate: leaderAssignment.publisher?.birthDate,
        gender: leaderAssignment.publisher?.gender,
        hasVehicle: Boolean(leaderAssignment.hasVehicle),
      }
    );
  }, []);

  const callPublisher = useCallback(
    (publisher: { phone: string }, location = window.location) => {
      location.href = getPhoneNumberUri(publisher.phone);
    },
    []
  );

  const shiftLeader = getShiftLeader(turn);
  const isShiftLeader = !isFetching && shiftLeader?.id === publisher?.id;
  const shiftEndedBeforeToday =
    !isFetching && moment(shift?.endDate).isBefore(moment().startOf('day'));
  const submissionDeadlineExceeded =
    !isFetching &&
    moment(shift?.endDate).isBefore(moment().subtract(6, 'days'));

  return (
    <div className={styles.wrapper}>
      <Toolbar
        style={{
          backgroundColor: theme.palette.background.paper,
          paddingLeft: theme.spacing(1),
          paddingRight: theme.spacing(1),
        }}
      >
        <IconButton
          component={Link}
          to={`/application/${params.year}/${params.week}/${params.day}`}
          size="large"
        >
          <CloseIcon />
        </IconButton>
        <Typography style={{ marginLeft: theme.spacing(2), flexGrow: 1 }}>
          {!isFetching &&
            `${moment(shift?.startDate).format('llll')} - ${moment(shift?.endDate).format('LT')}`}
        </Typography>
        {publisherIsAssignedForThisShift &&
        shiftTookPlace &&
        reportingSettings?.isExperiencesFeatureEnabled ? (
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/experiences/new?turnId=${turn?.id}`}
          >
            <FormattedMessage {...messages.reportExperience} />
          </Button>
        ) : null}
      </Toolbar>
      {isFetching ? (
        <div
          style={{ backgroundColor: grey[300] }}
          className={classnames(styles.wrapper, styles.loading)}
        >
          <CircularProgress size={60} />
        </div>
      ) : (
        <Spacings.Stack scale="m" className={styles.content}>
          {publisherIsAssignedForThisShift && turn ? (
            <>
              {turn.isCanceled && (
                <CancellationCard
                  reason={turn.cancellationReason || undefined}
                />
              )}
              {masterDataSettings && shift && shiftLeader ? (
                <ShiftLeaderCard
                  currentPublisherIsLeader={isShiftLeader}
                  shiftLeader={shiftLeader}
                  turn={turn}
                  shift={shift}
                  shiftIsCanceled={turn.isCanceled}
                  submissionDeadlineExceeded={submissionDeadlineExceeded}
                  allowTurnLeaderToCancelTurn={Boolean(
                    planningSettings?.allowTurnLeaderToCancelTurn
                  )}
                  settings={{
                    masterData: masterDataSettings as {
                      defaultRegionCallingCode: string;
                    },
                  }}
                />
              ) : null}
              <LocationCard
                route={turn.route.name}
                meetingPlace={turn.route.meetingPlace}
                depot={turn.route.depot}
              />
              {shiftLeader &&
              (isShiftLeader ||
                planningSettings?.provideTeamInfoToAllParticipants) ? (
                <AssignmentListCard
                  shiftLeader={shiftLeader}
                  userIsShiftLeader={isShiftLeader}
                  assignedPublishers={turn.turnAssignments
                    .map((assignment) => ({
                      ...assignment.publisherMiniRecord,
                      experienceLevel: assignment.publisher?.experienceLevel,
                      birthDate: assignment.publisher?.birthDate,
                      gender: assignment.publisher?.gender,
                      hasVehicle: Boolean(assignment.hasVehicle),
                    }))
                    .filter((pub) => !shiftLeader || pub.id !== shiftLeader.id)}
                />
              ) : null}
              {planningSettings?.provideTeamInfoToAllParticipantsOfPrecedingAndFollowingShifts ? (
                <PrecedingAndFollowingTeams
                  settings={{
                    masterData: masterDataSettings as {
                      defaultRegionCallingCode: string;
                    },
                  }}
                  turnId={turn.id}
                  onPublisherClick={callPublisher}
                />
              ) : null}
              {isShiftLeader && !shiftEndedBeforeToday ? (
                <ShiftsTurnLeadersCard
                  settings={{
                    masterData: masterDataSettings as {
                      defaultRegionCallingCode: string;
                    },
                  }}
                  onPublisherClick={callPublisher}
                  turnId={turn.id}
                />
              ) : null}
            </>
          ) : (
            <Spacings.Inset scale="s">
              <Typography variant="subtitle1">
                <FormattedMessage {...messages.noAssignmentForThisShift} />
              </Typography>
            </Spacings.Inset>
          )}
        </Spacings.Stack>
      )}
    </div>
  );
};

export default ApplicationShiftDetails;
